import { MetroDetail } from "@bumble/app/__generated__/data-contracts";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box
} from '@mui/material';
import { theme } from '@bumble/app/theme';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TabGrid, TabGridStructure } from '@bumble/app/components/TabGrid';
import NoData from '@bumble/app/components/NoData';
import { MetroInterfaces } from "@bumble/app/types/metroTypes";
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import { GridColDef } from '@mui/x-data-grid';
import getDate from '@bumble/app/utils/get-date';
import { Indicator } from '@bumble/app/components/Indicator';

const accordionStyles = {
  boxShadow: 'none',
  '&::before': {
    display: 'none',
  },
};

const accordionSummaryStyles = {
  flexDirection: 'row-reverse',
  fontWeight: 700,
  '& .MuiAccordionSummary-expandIconWrapper': {
    transform: 'rotate(-90deg)',
    color: theme.palette.primary.main,
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(0deg)'
  },
}

interface MetroDetailRow {
  id: string;
  name: string;
  intf: string;
  cidr_addr: string;
  vlan_mgmt?: string;
  vlan_q_in_q?: string;
  vlan_user_data?: string;
  sfp_type?: string;
}

const columns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
  {
    field: 'intf',
    headerName: 'Interface',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'cidr_addr',
    headerName: 'CIDR IP Address',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'vlan_user_data',
    headerName: 'User data VLAN ID',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'vlan_q_in_q',
    headerName: 'Q in Q VLAN ID',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'vlan_mgmt',
    headerName: 'Mgmt VLAN ID',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'sfp_type',
    headerName: 'SFP Type',
    width: 200,
    disableColumnMenu: true,
  },
];

export const EthernetDeviceTabDetails = ({ data }: { data: MetroDetail | undefined }) => {
  if (data === undefined) return <NoData />

  let rows: MetroDetailRow[] = [];

  const ports = data?.ports as MetroInterfaces
  for (let i in ports) {
    let { vlan_ids, name, intf, cidr_addr, sfp_type } = ports[i];
    let { vlan_mgmt, vlan_user_data, vlan_q_in_q } = vlan_ids as {
      vlan_mgmt?: string;
      vlan_user_data?: string;
      vlan_q_in_q?: string;
    };
    rows.push({
      id: i, name, intf, cidr_addr, vlan_mgmt, vlan_user_data, vlan_q_in_q, sfp_type
    })
  }

  const columnVisible = {
    name: true,
    intf: true,
    cidr_addr: true,
    vlan_mgmt: true,
    vlan_user_data: true,
    vlan_q_in_q: true,
    sfp_type: data.prod_code === 'BN-METRO-1000F',
  };

  const tabDataGeneral: TabGridStructure[] = [
    { label: 'Metro name', content: data?.metro_name || 'N/A' },
    { label: 'Product code', content: data?.prod_code || 'N/A' },
    { label: 'Account Id', content: data?.account_id || 'N/A' },
    { label: 'Metro Id', content: data?.metro_id || 'N/A' },
    { label: 'Description', content: data?.description || 'N/A' },
    { label: 'WAN CIR (Mbps)', content: data?.wan_cir_mbps?.toString() || 'N/A' },
    { label: 'WAN EIR (Mbps)', content: data?.wan_eir_mbps?.toString() || 'N/A' },
    { label: 'Created at', content: data !== undefined && data?.created_at ?
      getDate(data?.created_at) : 'N/A' },
    { label: 'Shipping address', content: data?.shipping_address || 'N/A' },
    { label: 'Site address', content: data?.site_address || 'N/A' },
    { label: 'Admin State', content: data?.admin_state || 'N/A' },
    { label: 'OP State', content: data?.op_state !== undefined ?
      <Indicator active={data?.op_state === 'up'} /> : 'N/A' },
    { label: 'Admin State Change at', content: data !== undefined &&
      data?.admin_state_change_at ? getDate(data?.admin_state_change_at) : 'N/A' },
    { label: 'OP State Change at', content: data !== undefined &&
      data?.op_state_change_at ? getDate(data?.op_state_change_at) : 'N/A' },
    { label: 'Provider Service ID', content: data?.provider_service_id || 'N/A' },
    { label: 'Client Circuit ID', content: data?.client_circuit_id || 'N/A' },
    { label: 'Latitude', content: data?.latitude || 'N/A' },
    { label: 'Longitude', content: data?.longitude || 'N/A' },
    { label: 'Region', content: data?.state || 'N/A' },
    { label: 'Entity ID', content: data?.entity_id || 'N/A' },
    { label: 'Local Contact Name', content: data?.contact_name || 'N/A' },
    { label: 'Local Contact Email', content: data?.contact_email || 'N/A' },
  ];

  return (
    <Box>
      <Accordion sx={accordionStyles}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={accordionSummaryStyles}
        >
          General
        </AccordionSummary>
        <AccordionDetails>
          <TabGrid data={tabDataGeneral} />
        </AccordionDetails>
      </Accordion>
      <Accordion sx={accordionStyles}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={accordionSummaryStyles}
        >
          Interfaces
        </AccordionSummary>
        <AccordionDetails>
          <DataTableGrid
            rows={rows}
            columns={columns}
            columnVisibilityModel={columnVisible}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}
